import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Alert from '~/Common-Objects/UI/Alert'
import Column from '~/Common-Objects/Grids/Column/Column'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import NewUserForm from '~/Common-Objects/NewUserForm/newUserForm.jsx'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Row from '~/Common-Objects/Grids/Row/Row'
import { EnableFeature as LabsEnable } from '~/Common-Objects/Labs/enableFeature'
import axios from '~/data/http/axios'
import LabQuestions from './questions'
import { alterFullpage, alterSiteDrawer } from '~/data/actions/siteActions'
import Lab from '~/Common-Objects/Labs/lab'

export default function Labs() {
    const dispatch = useDispatch()
    const account = useSelector((state) => state.accountMaster)
    const user = useSelector((state) => state.user)

    const [loading, setLoading] = useState(true)
    const [labs, setLabs] = useState([])
    const [overlay, setOverlay] = useState(undefined)

    const isAdmin = user?.userData?.roleId === 1

    const isApproved = account.accountMaster.approved && !account.accountMaster.deny

    const isInReview = !isApproved && account.accountMaster.doneNewUserQuestions

    const canEnableLabs = isAdmin && isApproved

    useEffect(() => {
        getLabs()
    }, [])

    const getLabs = async () => {
        try {
            let labsData = await axios.get('/accountmaster/labs')
            labsData = labsData.data
            setLabs(labsData)
            setLoading(false)
        } catch (err) {
            setLabs([])
            setLoading(false)
        }
    }

    const enable = (lab) => {
        if ((lab.closedBeta && lab.questions && lab.questions.length > 0) || lab.name === 'sms') {
            // show the drawer with questions
            const drawerContent = (
                <LabQuestions
                    lab={lab}
                    onClose={() => {
                        getLabs()
                    }}
                />
            )
            dispatch(alterSiteDrawer(true, true, 'right', drawerContent, true, 500, null, null, true))
        } else {
            // show the overlay
            const newOverlay = (
                <LabsEnable
                    labId={lab.id}
                    onClose={() => {
                        setOverlay(undefined)
                        getLabs()
                    }}
                />
            )
            setOverlay(newOverlay)
        }
    }

    const activateAccount = () => {
        dispatch(
            alterFullpage(
                <NewUserForm
                    from='labs'
                    finish={() => {
                        dispatch(alterFullpage(undefined))
                    }}
                />
            )
        )
    }

    if (loading) return <LoadingIcons iconType='2' />

    return (
        <>
            <Row>
                <Column
                    xs={12}
                    sm={12}
                    md={10}
                    lg={8}
                    className='col-md-offset-1 col-lg-offset-2'
                    style={{ display: 'flex', justifyContent: 'center' }}
                >
                    <Paper style={{ maxWidth: '880px' }}>
                        <div className='mar-b20'>
                            <h2 className='mar-b10'>Early Access Features</h2>
                            <span className='text-grey'>
                                Be the first to try what’s new and help build a better Transpond
                            </span>
                        </div>
                        {isApproved ? (
                            isAdmin ? (
                                <LabsDisclaimer />
                            ) : (
                                <AdminRequired />
                            )
                        ) : isInReview ? (
                            <AccountInReview />
                        ) : (
                            <AccountNotApproved activateAccount={activateAccount} />
                        )}
                        {!labs.length && <NoLabs />}

                        {!!labs.length &&
                            labs.map((lab, index) => {
                                return <Lab lab={lab} key={index} enable={enable} canEnableLabs={canEnableLabs} />
                            })}
                    </Paper>
                </Column>
            </Row>
            {overlay}
        </>
    )
}

function NoLabs() {
    return (
        <Paper className='paper-dotted'>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <h4 className='mar-b10'>No Features Available</h4>
                <div className='text-grey'>
                    There are currently no Early Access Features available to enable. Check back soon!
                </div>
            </div>
        </Paper>
    )
}

function AdminRequired() {
    return (
        <Alert
            type='info'
            className='mar-b20'
            header='Admin role required'
            content='Please contact your account admin to enable early access features.'
        />
    )
}

function LabsDisclaimer() {
    return (
        <Alert
            type='info'
            className='mar-b20'
            header='Early Access Disclaimer'
            content='Early access features are in development, may contain bugs, are subject to change without notice, and could become paid features if released. By enabling them, you accept these terms.'
        />
    )
}

function AccountInReview() {
    return (
        <Alert
            type='warning'
            className='mar-b20'
            header='Your account is under review'
            content='Thanks for submitting your approval request. We’re reviewing your details now and we’ll notify you as soon as your access is ready.'
        />
    )
}

function AccountNotApproved({ activateAccount }) {
    return (
        <Alert
            type='warning'
            className='mar-b20'
            header='Account approval is required to enable early access features'
            content='Most requests are reviewed automatically and only take a few minutes.'
            cta={
                <a onClick={() => activateAccount()} style={{ display: 'inline-flex' }}>
                    Request Approval
                    <Icons icon='arrowRight' />
                </a>
            }
        />
    )
}
